import { useState } from 'react';
import { log } from '../utils/protokoll';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { QrCode, Download, RefreshCw, Copy, Check, Printer } from 'lucide-react';
import { qrApi } from '../services/api';
import type { QRCodeData } from '../types/qr';

/** Escape HTML special characters to prevent XSS */
function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

interface QRCodeDisplayProps {
  elementId: number;
  elementName?: string;
  showActions?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

export function QRCodeDisplay({
  elementId,
  elementName,
  showActions = true,
  size = 'md',
}: QRCodeDisplayProps) {
  const [copied, setCopied] = useState(false);
  const queryClient = useQueryClient();

  const sizeClasses = {
    sm: 'w-24 h-24',
    md: 'w-40 h-40',
    lg: 'w-56 h-56',
  };

  const { data: qrData, isLoading, error } = useQuery<QRCodeData>({
    queryKey: ['qr', elementId],
    queryFn: () => qrApi.getForElement(elementId),
  });

  const regenerateMutation = useMutation({
    mutationFn: () => qrApi.generateForElement(elementId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['qr', elementId] });
    },
  });

  const handleCopyCode = async () => {
    if (qrData?.codeWert) {
      await navigator.clipboard.writeText(qrData.codeWert);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleDownloadSVG = async () => {
    try {
      const svg = await qrApi.getSVG(elementId);
      const blob = new Blob([svg], { type: 'image/svg+xml' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `qr-${qrData?.codeWert || elementId}.svg`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      log.error('Error downloading SVG:', err);
    }
  };

  const handlePrint = () => {
    if (!qrData?.dataUrl) return;

    const printWindow = window.open('', '_blank');
    if (printWindow) {
      const safeCodeWert = escapeHtml(qrData.codeWert);
      const safeDataUrl = escapeHtml(qrData.dataUrl);
      const safeElementName = elementName ? escapeHtml(elementName) : '';
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>QR-Code: ${safeCodeWert}</title>
          <style>
            body {
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              min-height: 100vh;
              margin: 0;
              font-family: Arial, sans-serif;
            }
            .qr-container {
              text-align: center;
              padding: 20px;
              border: 2px solid #000;
            }
            img { width: 200px; height: 200px; }
            .code { font-size: 14px; font-weight: bold; margin-top: 10px; }
            .name { font-size: 12px; color: #666; margin-top: 5px; }
          </style>
        </head>
        <body>
          <div class="qr-container">
            <img src="${safeDataUrl}" alt="QR Code" />
            <div class="code">${safeCodeWert}</div>
            ${safeElementName ? `<div class="name">${safeElementName}</div>` : ''}
          </div>
          <script>
            window.onload = function() {
              window.print();
              window.onafterprint = function() { window.close(); };
            };
          </script>
        </body>
        </html>
      `);
      printWindow.document.close();
    }
  };

  if (isLoading) {
    return (
      <div className={`${sizeClasses[size]} flex items-center justify-center bg-gray-100 rounded-lg animate-pulse`}>
        <QrCode className="w-8 h-8 text-gray-400" />
      </div>
    );
  }

  if (error || !qrData) {
    return (
      <div className={`${sizeClasses[size]} flex flex-col items-center justify-center bg-red-50 rounded-lg p-4`}>
        <QrCode className="w-8 h-8 text-red-400 mb-2" />
        <span className="text-xs text-red-600 text-center">QR-Code nicht verfügbar</span>
        {showActions && (
          <button
            onClick={() => regenerateMutation.mutate()}
            className="mt-2 text-xs text-primary hover:underline"
          >
            Neu generieren
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="inline-flex flex-col items-center">
      {/* QR Code Image */}
      <div className={`${sizeClasses[size]} bg-white rounded-lg border-2 border-gray-200 p-2`}>
        {qrData.dataUrl ? (
          <img
            src={qrData.dataUrl}
            alt={`QR-Code: ${qrData.codeWert}`}
            className="w-full h-full"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <QrCode className="w-1/2 h-1/2 text-gray-400" />
          </div>
        )}
      </div>

      {/* Code Value */}
      <div className="mt-2 text-sm font-mono text-gray-600 flex items-center gap-1">
        <span>{qrData.codeWert}</span>
        {showActions && (
          <button
            onClick={handleCopyCode}
            className="p-1 hover:bg-gray-100 rounded"
            title="Code kopieren"
          >
            {copied ? (
              <Check className="w-3.5 h-3.5 text-green-500" />
            ) : (
              <Copy className="w-3.5 h-3.5 text-gray-400" />
            )}
          </button>
        )}
      </div>

      {/* Action Buttons */}
      {showActions && (
        <div className="mt-3 flex items-center gap-2">
          <button
            onClick={handlePrint}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded transition-colors"
            title="QR-Code drucken"
          >
            <Printer className="w-3.5 h-3.5" />
            Drucken
          </button>
          <button
            onClick={handleDownloadSVG}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded transition-colors"
            title="Als SVG herunterladen"
          >
            <Download className="w-3.5 h-3.5" />
            SVG
          </button>
          <button
            onClick={() => regenerateMutation.mutate()}
            disabled={regenerateMutation.isPending}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded transition-colors disabled:opacity-50"
            title="QR-Code neu generieren"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${regenerateMutation.isPending ? 'animate-spin' : ''}`} />
          </button>
        </div>
      )}
    </div>
  );
}

export default QRCodeDisplay;
